import { get } from 'lodash';
import { fork, take, call, put } from 'redux-saga/effects';

import {
	SET_FILTER,
	STORE_LIST_REQUESTING,
	STORE_LIST_SUCCESS,
	STORE_LIST_ERROR,
	STORE_DETAIL_REQUESTING,
	STORE_DETAIL_SUCCESS,
	STORE_DETAIL_ERROR,
	UPDATE_STORE_REQUESTING,
	UPDATE_STORE_SUCCESS,
	UPDATE_STORE_ERROR,
	ADD_IMAGE_REQUESTING,
	ADD_IMAGE_SUCCESS,
	DELETE_IMAGE_REQUESTING,
	DELETE_IMAGE_SUCCESS,
	OTP_REQUESTING,
	OTP_REQUESTING_SUCCESS,
	OTP_REQUESTING_ERROR,
	VERIFY_OTP_REQUESTING,
	VERIFY_OTP_SUCCESS,
	VERIFY_OTP_ERROR,
	IMAGE_UPDATE_LOADER_TRUE,
	IMAGE_UPDATE_LOADER_FALSE,
	SEARCH_MORE_STORE_SUCCESS,
	TOTAL_STORE_LIST_REQUESTING,
} from '@/redux/ducks/manageStores/types';
import { INIT_APP_ERROR } from '@/redux/ducks/init/types';
import Api from '@/data';
import { SHOW_TOAST } from '@/redux/ducks/toast/types';

/** Function to get store list.
 * @param
 * payload - contains pageNo, pageSize, merchantId and search flag
 */
export function* fetchStoreList(payload) {
	try {
		yield put({
			type: SET_FILTER,
			payload,
		});
		const params = payload;
		if (payload.search) {
			params.active = true;
		}
		const response = yield call(Api.getStoreList, params);
		if (payload.search && payload.pageNo !== 1) {
			yield put({
				type: SEARCH_MORE_STORE_SUCCESS,
				data: response.data?.data,
			});
		} else {
			yield put({ type: STORE_LIST_SUCCESS, data: response.data?.data });
		}
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({ type: STORE_LIST_ERROR, error: error.data });
		}
	}
}

/** Function to get store list for infinite scroll and to make state stable
 * @param
 * payload - contains pageNo, pageSize, merchantId and search flag
 */
export function* fetchTotalStoreList(payload) {
	try {
		yield put({
			type: SET_FILTER,
			payload,
		});
		const response = yield call(Api.getStoreList, {
			...payload,
			active: true,
		});
		yield put({
			type: SEARCH_MORE_STORE_SUCCESS,
			data: response.data?.data,
		});
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({ type: STORE_LIST_ERROR, error: error.data });
		}
	}
}

/** Function to get store detail
 * @param
 * payload - merchantId and store id
 */
export function* fetchStoreDetail(payload) {
	try {
		yield put({ type: STORE_DETAIL_REQUESTING });
		const response = yield call(Api.getStoreDetail, payload);
		yield put({
			type: STORE_DETAIL_SUCCESS,
			data: {
				...response.data?.data,
			},
		});
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({ type: STORE_DETAIL_ERROR, error: error.data });
		}
	}
}

/** Function to get store detail
 * @param
 * payload - merchantId, store id and payload which contains data same as in get store detail
 */
export function* updateStoreData(payload) {
	try {
		const { merchantId, storeId } = payload;
		const response = yield call(
			Api.updateStore,
			merchantId,
			storeId,
			payload.payload,
		);
		yield put({ type: UPDATE_STORE_SUCCESS, data: response.data });
		yield put({
			type: SHOW_TOAST,
			payload: { msg: 'Data Saved Successfully.' },
		});
		yield call(fetchStoreDetail, { merchantId, storeId });
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({ type: UPDATE_STORE_ERROR, error: error.data });
			yield put({
				type: SHOW_TOAST,
				payload: { msg: get(error, 'data.message'), type: 'error' },
			});
		}
	}
}

/** Function to add image in store
 * @param
 * payload - image, merchantId, storeId, orderNo, stage, imageType and storeDetail
 */
export function* addStoreImage(payload) {
	try {
		yield put({
			type: IMAGE_UPDATE_LOADER_TRUE,
		});
		const {
			image,
			merchantId,
			storeId,
			orderNo,
			stage,
			imageType,
			storeDetail,
		} = payload;
		const response = yield call(
			Api.addStoreImage,
			image,
			merchantId,
			storeId,
			orderNo,
			stage,
			imageType,
		);

		yield put({
			type: ADD_IMAGE_SUCCESS,
			data: response.data,
		});
		yield put({
			type: SHOW_TOAST,
			payload: { msg: 'Image uploaded successfully.' },
		});
		/** to check from where the function is called, either from storeList
     or storeDetail page and call API accordingly */
		if (storeDetail) {
			yield call(fetchStoreDetail, { merchantId, storeId });
		} else {
			yield call(fetchStoreList, {
				pageNo: 1,
				pageSize: 20,
				merchantId,
			});
		}
		yield put({
			type: IMAGE_UPDATE_LOADER_FALSE,
		});
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({
				type: IMAGE_UPDATE_LOADER_FALSE,
			});
			yield put({
				type: SHOW_TOAST,
				payload: { msg: get(error, 'data.message'), type: 'error' },
			});
		}
	}
}

/** Function to add image in store
 * @param
 * payload - image, merchantId, storeId, orderNo, stage, imageType and storeDetail
 */
export function* deleteStoreImage(payload) {
	try {
		yield put({
			type: IMAGE_UPDATE_LOADER_TRUE,
		});
		const { merchantId, storeId, storeDetail } = payload;
		const response = yield call(Api.deleteStoreImage, payload);
		yield put({
			type: DELETE_IMAGE_SUCCESS,
			data: response.data,
		});
		yield put({
			type: SHOW_TOAST,
			payload: { msg: 'Image Deleted Successfully.' },
		});
		/** to check from where the function is called, either from storeList
     or storeDetail page and call API accordingly */
		if (storeDetail) {
			yield call(fetchStoreDetail, { merchantId, storeId });
		} else {
			yield call(fetchStoreList, {
				pageNo: 1,
				pageSize: 20,
				merchantId,
			});
		}
		yield put({
			type: IMAGE_UPDATE_LOADER_FALSE,
		});
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({
				type: IMAGE_UPDATE_LOADER_FALSE,
			});
			yield put({
				type: SHOW_TOAST,
				payload: { msg: get(error, 'data.message'), type: 'error' },
			});
		}
	}
}

/** Function to get otp for updating phone number
 * @param
 * phoneNumber: string
 */
export function* requestPhoneUpdateOtp(phoneNumber) {
	try {
		const response = yield call(Api.getStorePhoneUpdateOtp, phoneNumber);
		yield put({
			type: OTP_REQUESTING_SUCCESS,
			data: response.data.data,
		});
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({
				type: OTP_REQUESTING_ERROR,
				error: error.data,
			});
			yield put({
				type: SHOW_TOAST,
				payload: { msg: get(error, 'data.message'), type: 'error' },
			});
		}
	}
}

/** Function to verify otp for updating phone number
 * @param
 * payload: contains  merchantId, otp, phoneNumber, storeId and token
 */
export function* verifyPhoneUpdateOtp(payload) {
	try {
		const { merchantId, storeId } = payload;
		const response = yield call(Api.verifyStorePhoneUpdateOtp, payload);
		yield put({
			type: SHOW_TOAST,
			payload: { msg: 'Phone Number Updated.', type: 'info' },
		});
		yield put({
			type: VERIFY_OTP_SUCCESS,
			data: response.data,
		});
		yield call(fetchStoreDetail, { merchantId, storeId });
	} catch (error) {
		if (error.status === 401) {
			yield put({ type: INIT_APP_ERROR, error: error.data });
		} else {
			yield put({
				type: VERIFY_OTP_ERROR,
				error: error.data,
			});
			yield put({
				type: SHOW_TOAST,
				payload: { msg: get(error, 'data.message'), type: 'error' },
			});
		}
	}
}

export function* fetchStoreListWatcher() {
	while (true) {
		const { payload } = yield take(STORE_LIST_REQUESTING);
		yield call(fetchStoreList, payload);
	}
}

export function* fetchTotalStoreListWatcher() {
	while (true) {
		const { payload } = yield take(TOTAL_STORE_LIST_REQUESTING);
		yield call(fetchTotalStoreList, payload);
	}
}

export function* fetchStoreDetailWatcher() {
	while (true) {
		const { payload } = yield take(STORE_DETAIL_REQUESTING);
		yield call(fetchStoreDetail, payload);
	}
}

export function* updateStoreDataWatcher() {
	while (true) {
		const { payload } = yield take(UPDATE_STORE_REQUESTING);
		yield call(updateStoreData, payload);
	}
}

export function* addStoreImageWatcher() {
	while (true) {
		const { payload } = yield take(ADD_IMAGE_REQUESTING);
		yield call(addStoreImage, payload);
	}
}

export function* deleteStoreImageWatcher() {
	while (true) {
		const { payload } = yield take(DELETE_IMAGE_REQUESTING);
		yield call(deleteStoreImage, payload);
	}
}

export function* requestPhoneUpdateOtpWatcher() {
	while (true) {
		const { phoneNumber } = yield take(OTP_REQUESTING);
		yield call(requestPhoneUpdateOtp, phoneNumber);
	}
}

export function* verifyPhoneUpdateOtpWatcher() {
	while (true) {
		const { payload } = yield take(VERIFY_OTP_REQUESTING);
		yield call(verifyPhoneUpdateOtp, payload);
	}
}

function* storeListSaga() {
	yield fork(fetchStoreListWatcher);
	yield fork(fetchTotalStoreListWatcher);
	yield fork(fetchStoreDetailWatcher);
	yield fork(updateStoreDataWatcher);
	yield fork(addStoreImageWatcher);
	yield fork(deleteStoreImageWatcher);
	yield fork(requestPhoneUpdateOtpWatcher);
	yield fork(verifyPhoneUpdateOtpWatcher);
}

export default storeListSaga;
